import cx from 'classnames';
import { camelCase } from 'lodash';
import React, { Fragment } from 'react';
import ReactTable from 'react-table';
import { compose } from 'recompose';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import { INDETERMINATE } from '../../../../common/constants';
import Money from '../../../../common/money';
import SimpleCheckbox from '../../../../common/simpleCheckbox';
import withExpandedService from './withExpandedService';
import Tooltip from '../../../../common/PortalledTooltip';
import { t } from '../../../../../modules/infra/i18n';

const EXPANDER_CLASS = 'js-expand-service';

const resolveData = (data) => {
  const resolvedData = [];

  data.forEach((service) => {
    resolvedData.push(service);

    if (service.shopIds && service.shopIds.length > 2) {
      service.shopIds.forEach((shopId) => {
        if (shopId === 'all') {
          return;
        }

        const { shopIds, ...shopService } = service;

        resolvedData.push({
          ...shopService,
          shopId,
        });
      });
    }
  });

  return resolvedData;
};

const NoServicesFound = () => (
  <div className="c-heading c-heading--small">
    {t('barber:brand_services.no_services')}
  </div>
);

const DurationField = (props) => {
  const duration = props.input.value;

  if (!duration) {
    return '';
  }

  if (duration === INDETERMINATE) {
    return t('barber:brand_services.varies');
  }

  return `${duration} ${t('barber:brand_services.min')}`;
};

const CostField = (props) => {
  const cost = props.input.value;

  if (!cost) {
    return '';
  }

  if (cost === INDETERMINATE) {
    return t('barber:brand_services.varies');
  }

  return <Money cents={cost} />;
};

const BrandServicesForm = (props) => {
  const { expandedServices, toggleExpandedService, services, shopNamesById } = props;

  const columns = [
    {
      Header: t('barber:brand_services.columns.service_name'),
      accessor: 'name',
      Cell: (row) => {
        if (row.original.shopId && !row.original.singleShopService) {
          return <strong>{shopNamesById[row.original.shopId]}</strong>;
        }

        return (
          <Fragment>
            <strong>{row.value}</strong>
            <span>
              {
                  row.original.shopIds.length > 2
                    ? `${row.original.shopIds.length - 1} ${t('barber:brand_services.locations_available')}`
                    : `${shopNamesById[row.original.shopIds[1]]}`
                }
            </span>
          </Fragment>
        );
      },
    },
    {
      Header: t('barber:brand_services.columns.type'),
      id: 'type',
      width: 100,
      // eslint-disable-next-line no-confusing-arrow
      accessor: service => service.shopName ? '' : service.type,
    },
    {
      Header: t('barber:brand_services.columns.cost'),
      accessor: 'cost',
      width: 100,
      // eslint-disable-next-line no-confusing-arrow
      Cell: (row) => {
        const rootRowShopId = !Array.isArray(row.original.shopIds) || row.original.shopIds.length > 2
          ? 'all'
          : row.original.shopIds[1];

        const shopId = row.original.shopId || rootRowShopId;

        return (
          <Field
            id={`${row.original.id}-${shopId}.cost`}
            name={`${shopId}.services.${row.original.id}.cost`}
            component={CostField}
          />
        );
      },
    },
    {
      Header: t('barber:brand_services.columns.duration'),
      accessor: 'duration',
      width: 100,
      // eslint-disable-next-line no-confusing-arrow
      Cell: (row) => {
        const rootRowShopId = !Array.isArray(row.original.shopIds) || row.original.shopIds.length > 2
          ? 'all'
          : row.original.shopIds[1];

        const shopId = row.original.shopId || rootRowShopId;

        return (
          <Field
            id={`${row.original.id}-${shopId}.duration`}
            name={`${shopId}.services.${row.original.id}.duration`}
            component={DurationField}
          />
        );
      },
    },
    {
      Header: t('barber:brand_services.columns.assigned'),
      accessor: 'assigned',
      width: 100,
      Cell: (row) => {
        const isRootRow = Array.isArray(row.original.shopIds) && row.original.shopIds.length > 0;

        const rootRowShopId = !Array.isArray(row.original.shopIds) || row.original.shopIds.length > 2
          ? 'all'
          : row.original.shopIds[1];

        const shopId = row.original.shopId || rootRowShopId;

        return (
          <div className="u-flex u-align-items-center">
            <SimpleCheckbox
              key={`${shopId}.services.${row.original.id}.assigned`}
              id={`${row.original.id}-${shopId}.assigned`}
              name={`${shopId}.services.${row.original.id}.assigned`}
            />
            <Tooltip
              className="u-type--no-transform c-info-tooltip-new--form"
              display-if={isRootRow && !row.original.enabled}
            >
              <div className="u-m--">
                {t('barber:brand_services.tooltip_not_enabled')}
              </div>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  return (
    <div className="s-v2">
      <div className="c-heading c-heading--lightgrey c-wrapper u-pv0 u-mb-">{t('barber:brand_services.heading')}</div>
      <div className="c-wrapper-horizontal u-scrollable-x c-brand-barber-services-table">
        <ReactTable
          className="c-table-card c-table-new c-overflow-visible c-header-overflow-visible"
          sortable={false}
          columns={columns}
          NoDataComponent={NoServicesFound}
          minRows={0}
          showPagination={false}
          getTrGroupProps={(_, row) => {
            const { shopIds, id } = row.original;

            if (!shopIds) {
              return {
                'data-automation-id': `service-row:${camelCase(row.original.name)}`,
                className: cx(
                  'c-shop-row',
                  { 'is-hidden': !expandedServices.includes(id) }
                ),
              };
            }

            const onClick = (e) => {
              if (e.currentTarget.classList.contains(EXPANDER_CLASS)) {
                toggleExpandedService(id);
              }

              e.stopPropagation();
            };

            return {
              'data-automation-id': `service-row:${camelCase(row.original.name)}`,
              className: cx('c-service-row', {
                'c-service-row--expanded': expandedServices.includes(id),
                'c-service-row--empty': (shopIds || []).length === 2,
                [EXPANDER_CLASS]: (row.original.shopIds || []).length > 2,
              }),
              onClick,
            };
          }}
          data={services}
          resolveData={resolveData}
          pageSize={Number.MAX_SAFE_INTEGER}
        />
      </div>
    </div>
  );
};

BrandServicesForm.propTypes = {
  shopNamesById: PropTypes.object.isRequired,
  expandedServices: PropTypes.array.isRequired,
  toggleExpandedService: PropTypes.func.isRequired,
  services: PropTypes.array.isRequired,
};

export default compose(
  withExpandedService
)(BrandServicesForm);
